import * as THREE from 'three';
import { HealthBar } from '../../HealthBar.js';
import { colored } from '../../Style.js';
import {
  NAVY, paint, foamMaterial, smokeMaterial, bake, hullGeometry, wakeGeometry,
  flagGeometry, waveFlag, sailor, HATS, Buoyancy
} from './units-style.js';

// Hull plans. Every part stays within 0.95 of the collision radius (2.0 for the
// gunboat, 2.5 for the ironclad) at any roll, recoil or turret angle.
const GUNBOAT = { stern: -1.5, bow: 1.8, beam: 1.5, top: 0.55, sheer: 0.25, transom: 0.75 };
const IRONCLAD = { stern: -2.0, bow: 2.2, beam: 2.0, top: 0.42, sheer: 0.1, transom: 0.8 };

// Gunboat (tank): a steam gunboat with a team-painted hull, a wheelhouse, a
// funnel and a rotating deck cannon. The Ironclad (super tank) is a low
// armoured ship with an iron casemate, a twin-gun turret, gold trim and a
// smoke-belching funnel.
export class Tank {
  constructor(scene, unit, color, isSuper = false) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.isSuper = isSuper;
    this.mesh = null;
    this.turretGroup = null; // Separate group for turret rotation
    this.geometries = [];
    this.materials = [];
    this.puffs = [];
    this.lastPosition = null;
    this.targetRotation = 0;
    this.turretTargetRotation = 0;
    this.currentTurretRotation = 0;
    this.recoil = 0;
    this.healthBar = null;
    this.maxHealth = isSuper ? 90 : 30; // SuperTankHealth vs TankHealth
    this.create();
  }

  create() {
    const plan = this.isSuper ? IRONCLAD : GUNBOAT;
    this.mesh = new THREE.Group();

    // The model floats on the water; Buoyancy sets its height, roll and pitch
    this.model = new THREE.Group();
    this.mesh.add(this.model);

    const build = this.isSuper ? this.buildIronclad() : this.buildGunboat();
    this.addMesh(this.model, [this.track(hullGeometry({
      ...plan,
      side: this.color,
      trim: this.isSuper ? NAVY.gold : NAVY.white,
      keel: -0.35
    })), ...build.parts]);

    // Rotating deck gun; the barrel kicks back inside recoilGroup when it fires
    this.turretGroup = new THREE.Group();
    this.turretGroup.position.set(0, build.turret.y, build.turret.z);
    this.model.add(this.turretGroup);
    this.recoilGroup = new THREE.Group();
    this.turretGroup.add(this.recoilGroup);
    const gun = this.isSuper ? this.buildTwinTurret() : this.buildDeckGun();
    this.addMesh(this.turretGroup, gun.mount);
    this.addMesh(this.recoilGroup, gun.barrel);

    // Team flag at the stern
    this.flagMaterial = new THREE.MeshStandardMaterial({ color: this.color, roughness: 0.9, flatShading: true, side: THREE.DoubleSide });
    this.materials.push(this.flagMaterial);
    this.flag = new THREE.Mesh(this.track(flagGeometry(0.75, 0.48)), this.flagMaterial);
    this.flag.position.set(...build.flag);
    this.flag.rotation.y = Math.PI / 2;
    this.flag.castShadow = true;
    this.model.add(this.flag);

    // Funnel smoke
    for (let i = 0; i < (this.isSuper ? 5 : 3); i++) {
      const puff = new THREE.Mesh(this.track(new THREE.IcosahedronGeometry(this.isSuper ? 0.26 : 0.18, 0)), smokeMaterial());
      puff.userData.offset = i / (this.isSuper ? 5 : 3);
      this.model.add(puff);
      this.puffs.push(puff);
    }
    this.funnelTop = build.funnelTop;

    // Foam stays flat on the water, outside the rolling model
    this.water = new THREE.Group();
    this.mesh.add(this.water);
    this.wake = new THREE.Mesh(this.track(wakeGeometry({ ...plan, spread: 0.14, tail: 0.2 })), foamMaterial());
    this.wake.renderOrder = 1;
    this.water.add(this.wake);

    // Create health bar above the boat
    this.healthBar = new HealthBar(this.maxHealth, this.isSuper ? 3.6 : 3, 0.35);
    this.healthBar.getGroup().position.y = this.isSuper ? 3.2 : 3.1;
    this.mesh.add(this.healthBar.getGroup());

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.buoyancy = new Buoyancy(this.mesh, this.model, {
      bob: this.isSuper ? 0.035 : 0.05, roll: this.isSuper ? 0.025 : 0.04, pitch: this.isSuper ? 0.03 : 0.06
    });
    this.buoyancy.update();

    this.scene.add(this.mesh);
  }

  track(geometry) {
    this.geometries.push(geometry);
    return geometry;
  }

  addMesh(group, parts) {
    const mesh = new THREE.Mesh(this.track(bake(parts)), paint());
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    return mesh;
  }

  buildGunboat() {
    const team = this.color;
    const deckY = 0.47;
    const parts = [
      // Wheelhouse: white walls, dark windows, team roof
      colored(new THREE.BoxGeometry(0.82, 0.58, 0.7), NAVY.white, { y: deckY + 0.29, z: -0.3 }),
      colored(new THREE.BoxGeometry(0.84, 0.14, 0.12), NAVY.coat, { y: deckY + 0.42, z: 0.06 }),
      colored(new THREE.BoxGeometry(0.96, 0.08, 0.84), team, { y: deckY + 0.62, z: -0.3 }),
      // Funnel with a team band and black cap
      colored(new THREE.CylinderGeometry(0.17, 0.19, 0.95, 8), NAVY.white, { y: deckY + 0.48, z: -0.95 }),
      colored(new THREE.CylinderGeometry(0.18, 0.18, 0.2, 8), team, { y: deckY + 0.7, z: -0.95 }),
      colored(new THREE.CylinderGeometry(0.19, 0.17, 0.12, 8), NAVY.hat, { y: deckY + 0.98, z: -0.95 }),
      // Mast on the wheelhouse and a stern rail
      colored(new THREE.CylinderGeometry(0.03, 0.04, 1.3, 5), NAVY.woodDark, { y: deckY + 1.2, z: -0.55 }),
      colored(new THREE.BoxGeometry(0.06, 0.06, 0.6), NAVY.brass, { x: -0.55, y: deckY + 0.22, z: -1.0 }),
      colored(new THREE.BoxGeometry(0.06, 0.06, 0.6), NAVY.brass, { x: 0.55, y: deckY + 0.22, z: -1.0 }),
      // Coiled rope and a lifebuoy on the wheelhouse
      colored(new THREE.TorusGeometry(0.13, 0.05, 4, 8), NAVY.white, { x: 0.42, y: deckY + 0.32, z: -0.3, ry: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.16, 0.16, 0.08, 8), NAVY.rope, { x: -0.3, y: deckY + 0.04, z: -1.2 }),
      // Helmsman behind the window
      ...sailor({ coat: NAVY.coat, hat: HATS.cap, scale: 0.8, y: deckY, z: -0.4 })
    ];
    return { parts, turret: { y: deckY, z: 0.75 }, flag: [0, deckY + 1.83, -0.55], funnelTop: [0, deckY + 1.05, -0.95] };
  }

  // Deck gun aimed along local +Z: a pedestal and shield, and a barrel that recoils
  buildDeckGun() {
    const team = this.color;
    const mount = [
      colored(new THREE.CylinderGeometry(0.32, 0.38, 0.14, 8), NAVY.iron, { y: 0.07 }),
      colored(new THREE.BoxGeometry(0.22, 0.32, 0.3), NAVY.iron, { y: 0.3 }),
      colored(new THREE.BoxGeometry(0.62, 0.42, 0.06), NAVY.iron, { y: 0.42, z: 0.2, rx: -0.15 }),
      colored(new THREE.BoxGeometry(0.5, 0.12, 0.07), team, { y: 0.52, z: 0.22, rx: -0.15 })
    ];
    const barrel = [
      colored(new THREE.CylinderGeometry(0.11, 0.11, 0.3, 8), NAVY.iron, { y: 0.4, z: -0.05, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.06, 0.08, 0.8, 8), NAVY.hat, { y: 0.4, z: 0.45, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.08, 0.08, 0.08, 8), NAVY.brass, { y: 0.4, z: 0.84, rx: Math.PI / 2 })
    ];
    return { mount, barrel };
  }

  buildIronclad() {
    const team = this.color;
    const deckY = 0.36;
    const parts = [
      // Sloped iron casemate with rivet seams and a gold top rail
      colored(new THREE.CylinderGeometry(0.62, 0.85, 0.5, 4, 1).rotateY(Math.PI / 4), NAVY.iron, { y: deckY + 0.25, z: -0.75, scale: [1, 1, 1.5] }),
      colored(new THREE.BoxGeometry(0.92, 0.06, 1.3), team, { y: deckY + 0.52, z: -0.75 }),
      colored(new THREE.BoxGeometry(0.96, 0.04, 1.34), NAVY.gold, { y: deckY + 0.48, z: -0.75 }),
      // Funnel with a gold band
      colored(new THREE.CylinderGeometry(0.22, 0.25, 1.15, 8), NAVY.hat, { y: deckY + 1.05, z: -0.85 }),
      colored(new THREE.CylinderGeometry(0.24, 0.24, 0.14, 8), NAVY.gold, { y: deckY + 1.2, z: -0.85 }),
      colored(new THREE.CylinderGeometry(0.24, 0.24, 0.2, 8), team, { y: deckY + 0.9, z: -0.85 }),
      // Armoured conning tower behind the turret
      colored(new THREE.CylinderGeometry(0.24, 0.28, 0.4, 8), NAVY.iron, { y: deckY + 0.7, z: -0.15 }),
      colored(new THREE.BoxGeometry(0.4, 0.06, 0.1), NAVY.hat, { y: deckY + 0.8, z: 0.06 }),
      // Ram bow plate and stern mast
      colored(new THREE.BoxGeometry(0.08, 0.3, 0.5), NAVY.gold, { y: 0.25, z: 1.85 }),
      colored(new THREE.CylinderGeometry(0.035, 0.045, 1.5, 5), NAVY.woodDark, { y: deckY + 0.75, z: -1.3 })
    ];
    for (const z of [-1.3, -0.95, -0.6, -0.25]) {
      for (const sx of [-1, 1]) {
        parts.push(colored(new THREE.BoxGeometry(0.04, 0.32, 0.04), NAVY.hat, { x: sx * 0.53, y: deckY + 0.25, z, rz: sx * 0.31 }));
      }
    }
    return { parts, turret: { y: deckY, z: 0.85 }, flag: [0, deckY + 1.48, -1.3], funnelTop: [0, deckY + 1.7, -0.85] };
  }

  // Big iron turret with a team roof and twin barrels along +Z
  buildTwinTurret() {
    const team = this.color;
    const mount = [
      colored(new THREE.CylinderGeometry(0.62, 0.66, 0.5, 10), NAVY.iron, { y: 0.27 }),
      colored(new THREE.CylinderGeometry(0.6, 0.62, 0.06, 10), team, { y: 0.55 }),
      colored(new THREE.CylinderGeometry(0.67, 0.67, 0.06, 10), NAVY.gold, { y: 0.06 }),
      colored(new THREE.BoxGeometry(0.5, 0.12, 0.08), NAVY.hat, { y: 0.4, z: 0.62 })
    ];
    const barrel = [];
    for (const sx of [-1, 1]) {
      barrel.push(colored(new THREE.CylinderGeometry(0.09, 0.12, 1.0, 8), NAVY.hat, { x: sx * 0.2, y: 0.32, z: 0.9, rx: Math.PI / 2 }));
      barrel.push(colored(new THREE.CylinderGeometry(0.11, 0.11, 0.08, 8), NAVY.gold, { x: sx * 0.2, y: 0.32, z: 1.37, rx: Math.PI / 2 }));
    }
    return { mount, barrel };
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate body rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update body rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate body rotation
      let rotationDiff = this.targetRotation - this.mesh.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.mesh.rotation.y += rotationDiff * 0.1;

      // Smoothly interpolate turret rotation (relative to body)
      if (this.turretGroup) {
        const relativeTargetRotation = this.turretTargetRotation - this.mesh.rotation.y;
        let turretDiff = relativeTargetRotation - this.turretGroup.rotation.y;
        while (turretDiff > Math.PI) turretDiff -= Math.PI * 2;
        while (turretDiff < -Math.PI) turretDiff += Math.PI * 2;
        this.turretGroup.rotation.y += turretDiff * 0.15;
      }

      this.animate();

      // Store last position for next frame
      this.lastPosition = { x: position.x, y: position.y, z: position.z };
    }
  }

  // Floating, wake, flag, smoke and recoil
  animate() {
    const dt = this.buoyancy.update();
    const speed = this.buoyancy.speed;
    // The foam collar shows once under way and widens with speed
    this.wake.scale.set(1 + speed * 0.1, 1, 1);
    this.wake.visible = speed > 0.08;

    const t = performance.now() / 1000;
    waveFlag(this.flag, t + this.buoyancy.phase, 0.1);

    const [fx, fy, fz] = this.funnelTop;
    const rise = this.isSuper ? 1.3 : 0.9;
    for (const puff of this.puffs) {
      const age = (t * (this.isSuper ? 0.45 : 0.35) + puff.userData.offset) % 1;
      puff.position.set(fx, fy + age * rise, fz - age * 0.35);
      puff.scale.setScalar(Math.sin(age * Math.PI) * (0.7 + age));
    }

    if (dt > 0) {
      this.recoil = Math.max(0, this.recoil - dt * 4);
      this.recoilGroup.position.z = -(this.isSuper ? 0.22 : 0.16) * this.recoil * this.recoil;
    }
  }

  // Called when this boat fires: the barrel kicks back
  fire() {
    this.recoil = 1;
  }

  // Set target position for turret to aim at
  setTarget(targetPosition) {
    if (this.mesh && targetPosition) {
      const dx = targetPosition.x - this.mesh.position.x;
      const dz = targetPosition.z - this.mesh.position.z;
      this.turretTargetRotation = Math.atan2(dx, dz);
    }
  }

  // Aim turret at a world position
  aimAt(worldX, worldZ) {
    if (this.mesh) {
      const dx = worldX - this.mesh.position.x;
      const dz = worldZ - this.mesh.position.z;
      this.turretTargetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
    // Shared materials stay; the flag material and all geometry are ours
    this.geometries.forEach(geometry => geometry.dispose());
    this.materials.forEach(material => material.dispose());
    this.geometries = [];
    this.materials = [];
  }
}
